import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useGetMatterDetailsQuery,
  usePatchMatterClientMutation,
} from '@/services/api/mattersService';
import {
  type GenderValue,
  type MatterClient,
  PREFERRED_LANGUAGE_OPTIONS,
  type PreferredLanguageValue,
} from '@/services/types/matter-client-types';
import { sanitizePhoneNumber } from '@/utils/validation';

import { useUnifiedFlow } from '../../context/UnifiedFlowContext';
import { IngestionStepLayout } from '../../layout';
import {
  type ClientInformationFormData,
  clientInformationSchema,
} from '../../schemas';
import { IngestionStepSkeleton } from '../../skeletons';
import { handleDatePaste } from '../../utils';

/**
 * Extracts the last 4 digits from a full SSN.
 * Handles both formatted (123-45-6789) and unformatted (123456789) SSNs.
 * Returns null if SSN is not complete (9 digits).
 */
const extractLast4FromSSN = (ssn: string | undefined | null): string | null => {
  if (!ssn) return null;
  const digits = ssn.replace(/\D/g, '');
  if (digits.length !== 9) return null;
  return digits.slice(-4);
};

/**
 * Checks if the SSN has exactly 9 digits (complete SSN).
 */
const isCompleteSSN = (ssn: string | undefined | null): boolean => {
  if (!ssn) return false;
  return ssn.replace(/\D/g, '').length === 9;
};

interface ClientInformationStepProps {
  matterId: string;
  onSaveComplete?: (success: boolean) => void;
}

/**
 * ClientInformationStep - Client info step (NOT audit-tracked).
 * Collects client contact and demographic information.
 * This step is not tracked in the audit record.
 */
export const ClientInformationStep: React.FC<ClientInformationStepProps> = ({
  matterId,
  onSaveComplete,
}) => {
  const hasHydrated = useRef(false);

  // Fetch matter details to pre-populate client data
  const { data: matter, isLoading: isLoadingMatter } = useGetMatterDetailsQuery(matterId);
  const [patchClient] = usePatchMatterClientMutation();

  // Initialize form with real-time validation
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm<ClientInformationFormData>({
    resolver: zodResolver(clientInformationSchema),
    mode: 'onChange', // Validate on every change for real-time feedback
    defaultValues: {
      email: '',
      date_of_birth: '',
      gender: undefined,
      ssn: '',
      last_four_of_ssn: '',
      preferred_language: undefined,
      address: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        postcode: '',
      },
      phone_numbers: [{ phone_number: '' }],
    },
  });

  const phoneNumbers = watch('phone_numbers') || [];

  // Watch SSN for auto-population logic
  const watchedSSN = useWatch({ control, name: 'ssn' });
  const ssnIsComplete = isCompleteSSN(watchedSSN);

  // Pre-populate form from existing client data
  useEffect(() => {
    if (hasHydrated.current) return;
    if (matter?.clients?.[0]) {
      hasHydrated.current = true;
      const client = matter.clients[0];
      setValue('email', client.email || '');
      setValue('date_of_birth', client.date_of_birth || '');
      setValue('gender', client.gender);
      setValue('ssn', client.ssn || '');
      // Handle last_four_of_ssn with SSN derivation fallback
      if (client.last_four_of_ssn) {
        setValue('last_four_of_ssn', client.last_four_of_ssn);
      } else if (client.ssn && isCompleteSSN(client.ssn)) {
        const derived = extractLast4FromSSN(client.ssn);
        if (derived) {
          setValue('last_four_of_ssn', derived);
        }
      } else {
        setValue('last_four_of_ssn', '');
      }
      setValue('preferred_language', client.preferred_language);
      setValue('address', {
        line1: client.address?.line1 || '',
        line2: client.address?.line2 || '',
        city: client.address?.city || '',
        state: client.address?.state || '',
        postcode: client.address?.postcode || '',
      });
      // Phone numbers come from API in E.164 format (e.g. +13233345768)
      // We preserve the format for display and submission
      if (client.phone_numbers?.length > 0) {
        setValue('phone_numbers', client.phone_numbers);
      }
    }
  }, [matter, setValue]);

  // Auto-fill from CMS import data (runs once after hydration)
  const { importData } = useUnifiedFlow();
  const hasAutoFilledFromImport = useRef(false);
  useEffect(() => {
    if (hasAutoFilledFromImport.current) return;
    if (!importData?.payload?.client_info) return;
    // Wait for matter to load so hydration runs first
    if (!matter) return;

    hasAutoFilledFromImport.current = true;
    const cli = importData.payload.client_info;

    const tryFill = (
      field: { presence: string; value: unknown },
      formKey: keyof ClientInformationFormData,
      currentVal: unknown,
    ) => {
      if (field.presence !== 'present' || !field.value) return;
      if (currentVal) return; // Don't overwrite existing values
      setValue(formKey, String(field.value) as never);
    };

    tryFill(cli.email, 'email', watch('email'));
    tryFill(cli.date_of_birth, 'date_of_birth', watch('date_of_birth'));
    tryFill(cli.ssn_last_four, 'last_four_of_ssn', watch('last_four_of_ssn'));

    if (cli.phone_number?.presence === 'present' && cli.phone_number.value) {
      const currentPhones = watch('phone_numbers');
      const hasExistingPhone = currentPhones?.some(p => p.phone_number?.trim());
      if (!hasExistingPhone) {
        setValue('phone_numbers', [{ phone_number: String(cli.phone_number.value) }]);
      }
    }

    if (cli.address?.presence === 'present' && cli.address.value) {
      const currentAddr = watch('address');
      if (!currentAddr?.line1) {
        const addr = cli.address.value;
        setValue('address', {
          line1: addr.line1 || '',
          line2: addr.line2 || '',
          city: addr.city || '',
          state: addr.state || '',
          postcode: addr.postcode || '',
        });
      }
    }
  }, [importData, matter, setValue, watch]);

  // Auto-populate last 4 when SSN becomes complete
  useEffect(() => {
    if (ssnIsComplete) {
      const derivedLast4 = extractLast4FromSSN(watchedSSN);
      if (derivedLast4) {
        setValue('last_four_of_ssn', derivedLast4, { shouldValidate: true });
      }
    }
    // Note: We preserve last_four_of_ssn when SSN becomes incomplete (user can edit)
  }, [watchedSSN, ssnIsComplete, setValue]);

  const onSubmit = async (data: ClientInformationFormData) => {
    const primaryClient = matter?.clients?.[0];
    if (!primaryClient) {
      toast.warning('No client found for this matter. Skipping client info.');
      onSaveComplete?.(true);
      return;
    }

    // Process phone numbers: filter empty, sanitize to E.164 format
    // Backend expects E.164 format: +1XXXXXXXXXX
    const phoneNumbersToSave = data.phone_numbers
      ?.filter((p) => p.phone_number && p.phone_number.trim() !== '')
      .map((p) => {
        // If already E.164 format, keep as-is; otherwise sanitize
        const phoneNumber = p.phone_number.startsWith('+')
          ? p.phone_number
          : sanitizePhoneNumber(p.phone_number);
        return {
          ...(p.id ? { id: p.id } : {}),
          phone_number: phoneNumber,
        };
      });

    // Only send date_of_birth if it's a valid ISO date (YYYY-MM-DD)
    const dateOfBirth = /^\d{4}-\d{2}-\d{2}$/.test(data.date_of_birth || '')
      ? data.date_of_birth
      : null;

    // Only send SSN if it's complete (9 digits)
    const ssnDigits = data.ssn?.replace(/-/g, '') || '';
    const ssnValue = ssnDigits.length === 9 ? data.ssn : null;

    // Derive last_four from SSN at submission time if SSN is complete (ensures consistency)
    let lastFourSsn: string | null = null;
    if (ssnValue) {
      // SSN is complete - derive last 4 to ensure data consistency
      lastFourSsn = extractLast4FromSSN(ssnValue);
    } else if (data.last_four_of_ssn?.length === 4) {
      // SSN is incomplete/missing - use manual entry
      lastFourSsn = data.last_four_of_ssn;
    }

    try {
      // Note: first_name and last_name are managed from Basics step, not included here
      const patchData: Partial<
        Record<keyof ClientInformationFormData, unknown>
      > = {
        email: data.email,
        date_of_birth: dateOfBirth,
        gender: data.gender,
        ssn: ssnValue,
        last_four_of_ssn: lastFourSsn,
        preferred_language: data.preferred_language,
        address: data.address,
      };

      // Include phone_numbers if we have any
      if (phoneNumbersToSave && phoneNumbersToSave.length > 0) {
        patchData.phone_numbers = phoneNumbersToSave;
      }

      await patchClient({
        matter_id: matterId,
        matter_client_id: primaryClient.id,
        patch: patchData as Partial<MatterClient>,
      }).unwrap();

      toast.success('Client information saved');
      onSaveComplete?.(true);
    } catch {
      toast.error('Failed to save client information');
      onSaveComplete?.(false);
    }
  };

  const onInvalid = () => {
    toast.warning('Please fix the highlighted fields before continuing.');
  };

  const addPhoneNumber = () => {
    setValue('phone_numbers', [...phoneNumbers, { phone_number: '' }]);
  };

  const removePhoneNumber = (index: number) => {
    setValue(
      'phone_numbers',
      phoneNumbers.filter((_, i) => i !== index),
    );
  };

  // Show skeleton during initial load
  if (isLoadingMatter && !hasHydrated.current) {
    return <IngestionStepSkeleton />;
  }

  return (
    <IngestionStepLayout
      title="Client Information"
      description="Enter basic client contact and demographic information"
    >
      <form
        id="ingestion-step-form"
        onSubmit={handleSubmit(onSubmit, onInvalid)}
        className="space-y-2"
      >
          <Card>
            <CardContent className="space-y-2 p-2">
              {/* Contact Fields */}
              <div>
                <Label htmlFor="email" className="text-xs">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email')}
                  placeholder="john.doe@example.com"
                  className="h-8 text-xs"
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Phone Numbers */}
              <div>
                <Label className="text-xs">Phone Numbers</Label>
                <div className="space-y-1.5">
                  {phoneNumbers.map((_, index) => (
                    <div key={index}>
                      <div className="flex gap-1.5">
                        <Input
                          type="tel"
                          placeholder="555-123-4567"
                          {...register(`phone_numbers.${index}.phone_number`)}
                          className="h-8 flex-1 text-xs"
                        />
                        {phoneNumbers.length > 1 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs"
                            onClick={() => removePhoneNumber(index)}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                      {errors.phone_numbers?.[index]?.phone_number && (
                        <p className="mt-1 text-xs text-red-600">
                          {errors.phone_numbers[index]?.phone_number?.message}
                        </p>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={addPhoneNumber}
                  >
                    Add Phone Number
                  </Button>
                </div>
              </div>

              {/* Demographic Fields */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="date_of_birth" className="text-xs">
                    Date of Birth
                  </Label>
                  <Input
                    id="date_of_birth"
                    type="date"
                    {...register('date_of_birth')}
                    onPaste={(e) =>
                      handleDatePaste(e, (val) =>
                        setValue('date_of_birth', val),
                      )
                    }
                    className="h-8 text-xs"
                  />
                </div>
                <div>
                  <Label htmlFor="gender" className="text-xs">
                    Gender
                  </Label>
                  <Select
                    onValueChange={(value) => setValue('gender', value as GenderValue)}
                    value={watch('gender') ?? undefined}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* SSN Fields */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="ssn" className="text-xs">
                    Social Security Number
                  </Label>
                  <Input
                    id="ssn"
                    type="text"
                    inputMode="numeric"
                    maxLength={11}
                    placeholder="123-45-6789"
                    {...register('ssn')}
                    className="h-8 text-xs"
                  />
                </div>
                <div>
                  <Label htmlFor="last_four_of_ssn" className="text-xs">
                    Last 4 of SSN
                  </Label>
                  <Input
                    id="last_four_of_ssn"
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="1234"
                    disabled={ssnIsComplete}
                    {...register('last_four_of_ssn')}
                    className={`h-8 text-xs ${ssnIsComplete ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 cursor-not-allowed' : ''}`}
                  />
                  {ssnIsComplete && (
                    <p className="mt-0.5 text-[10px] text-zinc-500 dark:text-zinc-400">
                      Auto-filled from SSN
                    </p>
                  )}
                </div>
              </div>

              {/* Language */}
              <div>
                <Label htmlFor="preferred_language" className="text-xs">
                  Preferred Language
                </Label>
                <Select
                  onValueChange={(value) =>
                    setValue('preferred_language', value as PreferredLanguageValue)
                  }
                  value={watch('preferred_language') ?? undefined}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    {PREFERRED_LANGUAGE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Address Fields */}
              <div className="space-y-1.5">
                <h3 className="text-xs font-semibold text-zinc-900">Address</h3>
                <div>
                  <Label htmlFor="address.line1" className="text-xs">
                    Street Address
                  </Label>
                  <Input
                    id="address.line1"
                    {...register('address.line1')}
                    placeholder="123 Main St"
                    className="h-8 text-xs"
                  />
                </div>
                <div>
                  <Label htmlFor="address.line2" className="text-xs">
                    Address Line 2 (Optional)
                  </Label>
                  <Input
                    id="address.line2"
                    {...register('address.line2')}
                    placeholder="Apt 4B"
                    className="h-8 text-xs"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label htmlFor="address.city" className="text-xs">
                      City
                    </Label>
                    <Input
                      id="address.city"
                      {...register('address.city')}
                      placeholder="New York"
                      className="h-8 text-xs"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address.state" className="text-xs">
                      State
                    </Label>
                    <Input
                      id="address.state"
                      {...register('address.state')}
                      placeholder="NY"
                      maxLength={2}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address.postcode" className="text-xs">
                      Zip Code
                    </Label>
                    <Input
                      id="address.postcode"
                      {...register('address.postcode')}
                      placeholder="10001"
                      className="h-8 text-xs"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </form>
    </IngestionStepLayout>
  );
};
